/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";
import React from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppProfileMenu from "components/AppProfileMenu";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  collapseItem,
  collapseIconBox,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { resetAuth } from "../../redux/slices/authSlice";
import { setLang } from "../../redux/slices/generalSlice";
import { store } from "../../redux/store";
import { performLogout } from "../../utils/logout";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import LinearProgress from "@mui/material/LinearProgress";
import { GetStorageSummaryCall } from "../../redux/api/report.api";
import { Padding } from "@mui/icons-material";
import { useUpcomingDemos } from "../../context/UpcomingDemosContext";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import Tooltip from "@mui/material/Tooltip";

const LANGUAGES = [
  "en", "fr", "ko", "pt", "pt-br", "es", "it", "ge", "nl", "zh-cn", "zh-tw", "ja",
  "ar", "kk", "tk", "tr", "fa", "hi", "id", "ro", "ru", "vi", "ms", "tl", "no", "da", "sv", "el", "pl", "cs", "fi", "hu",
];

function Sidenav({
  color,
  university = null,
  brand,
  brandName,
  routes,
  ...rest
}) {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    sidenavColor,
  } = controller;
  const location = useLocation();
  const { t } = useTranslation();
  const reduxDispatch = useDispatch();
  const lang = useSelector((state) => state.general?.lang) || "en";
  const role = useSelector((state) => state.auth?.role);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);
  const [storage, setStorage] = useState({
    usedBytes: 0,
    limitBytes: 0,
    percent: 0,
    licenseStart: null,
    licenseEnd: null,
  });
  const collapseName = location.pathname.replace("/", "");

  /*
    Upcoming demos badge: live count + click-to-open modal on the
    "Demo requests" sidebar row. Provider returns enabled=false for
    non-admins so the badge silently hides for everyone else.
  */
  const upcomingDemos = useUpcomingDemos();

  // Expand/collapse state for parent items with nested routes (e.g. Quiz)
  const [openCollapse, setOpenCollapse] = useState(() => {
    const initial = {};
    routes.forEach((r) => {
      if (r.collapse && Array.isArray(r.collapse) && r.collapse.length > 0) {
        const isActive = r.collapse.some((c) => c.route && location.pathname.startsWith(c.route));
        initial[r.key] = !!isActive;
      }
    });
    return initial;
  });

  const toggleCollapse = (key) => {
    setOpenCollapse((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }
  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const getNavLabel = (navKey, fallbackName) => {
    const translated = t(`nav.${navKey}`);
    return translated === `nav.${navKey}` ? fallbackName : translated;
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav
      );
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
    // Do not depend on `location`: only viewport width matters; re-running on every route caused extra work.
  }, [dispatch]);

  useEffect(() => {
    const roleKey = String(role || "").toLowerCase();
    const shouldShowStorage = roleKey === "prof" || roleKey === "professor" || roleKey === "university_admin";
    if (!shouldShowStorage) return;
    let mounted = true;
    GetStorageSummaryCall().then((res) => {
      if (!mounted) return;
      setStorage({
        usedBytes: Number(res?.usedBytes || 0),
        limitBytes: Number(res?.limitBytes || 0),
        percent: Number(res?.percent || 0),
        licenseStart: res?.licenseStart || null,
        licenseEnd: res?.licenseEnd || null,
      });
    });
    return () => {
      mounted = false;
    };
  }, [role]);

  const formatBytesToGb = (bytes = 0) => {
    const gb = Number(bytes || 0) / (1024 * 1024 * 1024);
    return gb >= 1 ? gb.toFixed(2) : gb.toFixed(3);
  };

  const formatLicenseEnd = (licenseEnd) => {
    if (!licenseEnd) return null;
    const d = new Date(licenseEnd);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getLicenseProgress = (licenseStart, licenseEnd) => {
    const end = new Date(licenseEnd || "");
    if (Number.isNaN(end.getTime())) return { percent: 0, color: "#29b6f6", daysLeft: null };
    const start = new Date(licenseStart || "");
    const now = new Date();
    const msDay = 24 * 60 * 60 * 1000;
    const daysLeft = Math.ceil((end.getTime() - now.getTime()) / msDay);

    let percentElapsed = 0;
    if (!Number.isNaN(start.getTime()) && end.getTime() > start.getTime()) {
      const totalMs = end.getTime() - start.getTime();
      const elapsedMs = now.getTime() - start.getTime();
      if (elapsedMs <= 0) {
        percentElapsed = 0;
      } else if (elapsedMs >= totalMs) {
        percentElapsed = 100;
      } else {
        percentElapsed = (elapsedMs / totalMs) * 100;
      }
    } else {
      // Fallback when start date is missing: assume a 365-day window ending at license end
      const fallbackStart = new Date(end.getTime() - 365 * msDay);
      const totalMs = end.getTime() - fallbackStart.getTime();
      const elapsedMs = now.getTime() - fallbackStart.getTime();
      percentElapsed = Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
    }

    // Bar fill = elapsed share of [license_start → license_end] (empty at start, full when expired)
    const percent = Math.min(100, Math.max(0, percentElapsed));
    const color = daysLeft <= 14 ? "#ef5350" : daysLeft <= 45 ? "#fbc02d" : "#66bb6a";
    return { percent, color, daysLeft };
  };
  const licenseProgress = getLicenseProgress(storage.licenseStart, storage.licenseEnd);


  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map((route) => {
    const { type, name, icon, title, noCollapse, key, href, route: routePath, collapse } = route;
    let returnValue;

    if (type === "collapse" && collapse && Array.isArray(collapse) && collapse.length > 0) {
      const isOpen = openCollapse[key] !== false;
      const isActive = collapse.some((c) => c.route && location.pathname === c.route);
      returnValue = (
        <React.Fragment key={key}>
          <ListItem
            component="li"
            disablePadding
            onClick={() => toggleCollapse(key)}
            sx={{ cursor: "pointer" }}
          >
            <MDBox
              sx={(theme) =>
                collapseItem(theme, {
                  active: isActive,
                  transparentSidenav,
                  whiteSidenav,
                  darkMode,
                  sidenavColor,
                })
              }
              width="100%"
              display="flex"
              alignItems="center"
            >
              <ListItemIcon
                sx={(theme) => ({
                  ...collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active: isActive }),
                  "& .MuiIcon-root": { color: "white !important" },
                  "& svg": { color: "white !important" },
                })}
              >
                {typeof icon === "string" ? <Icon fontSize="small">{icon}</Icon> : icon}
              </ListItemIcon>
              <ListItemText
                primary={getNavLabel(key, name)}
                sx={(theme) =>
                  collapseText(theme, {
                    miniSidenav,
                    transparentSidenav,
                    whiteSidenav,
                    active: isActive,
                  })
                }
              />
              <Icon
                sx={{
                  fontSize: "1.25rem",
                  color: "white",
                  transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s",
                }}
              >
                expand_more
              </Icon>
            </MDBox>
          </ListItem>
          {isOpen &&
            collapse.map((child) => (
              <NavLink key={child.key} to={child.route} style={{ textDecoration: "none" }}>
                <SidenavCollapse
                  name={getNavLabel(child.key, child.name)}
                  icon={<Icon sx={{ fontSize: "0.75rem", opacity: 0.7 }}>chevron_right</Icon>}
                  active={location.pathname === child.route}
                  noCollapse
                  nested
                />
              </NavLink>
            ))}
        </React.Fragment>
      );
    } else if (type === "collapse") {
      const isActive = routePath
        ? location.pathname === routePath || location.pathname.startsWith(routePath + "/")
        : key === collapseName;
      const navNode = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={getNavLabel(key, name)}
            icon={icon}
            active={isActive}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink key={key} to={routePath}>
          <SidenavCollapse
            name={getNavLabel(key, name)}
            icon={icon}
            active={isActive}
          />
        </NavLink>
      );

      /*
        Upcoming-demos badge: overlay a click-target on the right edge of
        the "Demo requests" row. The badge stops propagation so clicking
        it opens the modal instead of navigating; tapping the rest of the
        row still navigates as before.
      */
      const showDemoBadge =
        key === "demo-requests" &&
        upcomingDemos.enabled &&
        Number(upcomingDemos.total || 0) > 0;
      if (showDemoBadge) {
        const count = Number(upcomingDemos.total || 0);
        const todayCount = Number(upcomingDemos.todayCount || 0);
        const badgeLabel =
          todayCount > 0
            ? `${todayCount} today${count > todayCount ? ` · ${count} total` : ""}`
            : `${count} upcoming`;
        const isUrgent = todayCount > 0;
        const handleBadgeClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          upcomingDemos.openModal();
        };
        returnValue = (
          <MDBox key={key} sx={{ position: "relative" }}>
            {navNode}
            <Tooltip title={`${badgeLabel} — click to view`} placement="right">
              <MDBox
                onClick={handleBadgeClick}
                sx={(theme) => ({
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: miniSidenav ? "none" : "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 0.9,
                  py: 0.25,
                  borderRadius: 999,
                  cursor: "pointer",
                  zIndex: 2,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: isUrgent ? "#fff" : "#fff",
                  background: isUrgent
                    ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`
                    : `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                  boxShadow: isUrgent
                    ? `0 0 0 0 ${theme.palette.error.main}66`
                    : `0 4px 10px ${theme.palette.primary.main}33`,
                  animation: isUrgent ? "demoBadgePulse 1.6s ease-in-out infinite" : "none",
                  "@keyframes demoBadgePulse": {
                    "0%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}66` },
                    "70%": { boxShadow: `0 0 0 6px ${theme.palette.error.main}00` },
                    "100%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
                  },
                  transition: "transform 0.15s ease",
                  "&:hover": { transform: "translateY(-50%) scale(1.05)" },
                })}
              >
                <EventAvailableIcon sx={{ fontSize: 13, color: "#fff" }} />
                {todayCount > 0 ? `${todayCount} today` : `${count}`}
              </MDBox>
            </Tooltip>
            {/* Mini-sidenav: tiny pulsing dot in the corner of the icon */}
            {miniSidenav ? (
              <MDBox
                onClick={handleBadgeClick}
                sx={(theme) => ({
                  position: "absolute",
                  top: 6,
                  left: 24,
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  cursor: "pointer",
                  zIndex: 2,
                  background: isUrgent
                    ? theme.palette.error.main
                    : theme.palette.info.main,
                  boxShadow: isUrgent
                    ? `0 0 0 0 ${theme.palette.error.main}80`
                    : `0 0 0 2px ${theme.palette.background.paper || "#fff"}`,
                  animation: isUrgent ? "demoBadgeDot 1.6s ease-in-out infinite" : "none",
                  "@keyframes demoBadgeDot": {
                    "0%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}80` },
                    "70%": { boxShadow: `0 0 0 5px ${theme.palette.error.main}00` },
                    "100%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
                  },
                })}
              />
            ) : null}
          </MDBox>
        );
      } else {
        returnValue = navNode;
      }
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox
        display="flex"
        flexDirection="column"
        height="100%"
        maxHeight="100%"
        minHeight={0}
        overflow="hidden"
        flex={1}
      >
        <MDBox pt={1.5} pb={0.5} px={0.75} textAlign="center" flexShrink={0}>
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.25}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <>
            {university?.logo_url ? (
              <MDBox
                component="img"
                src={university?.logo_url}
                alt="Brand"
                sx={{
                  maxHeight: 70,
                  maxWidth: "50%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                  mx: "auto",
                  marginTop: 1
                }}
              />
            ) : (
              <MDBox
                component={NavLink}
                to="/"
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                  "&:focus, &:focus-visible": { border: "none", outline: "none", boxShadow: "none" },
                }}
              >
                <MDBox
                  component="img"
                  src={require("../../assets/images/logos/reverse-logo.png")}
                  alt="Brand"
                  sx={{ marginTop: 1, width: brandName ? "4rem" : "30%", minWidth: brandName ? "3.5rem" : "auto", maxWidth: "50%", objectFit: "contain" }}
                />
                {brandName ? (
                  <MDBox
                    width="100%"
                    sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
                  >
                    <MDTypography
                      component="h6"
                      variant="button"
                      fontWeight="medium"
                      color={textColor}
                    >
                      {brandName}
                    </MDTypography>
                  </MDBox>
                ) : null}
              </MDBox>
            )}
          </>
        </MDBox>
        {/* {university?.name && (
        <MDBox px={2} mt={1} mb={0.5} textAlign="center">
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={textColor}
            sx={{ opacity: 0.8 }}
          >
            {university?.name}
          </MDTypography>
        </MDBox>
      )} */}
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
          sx={{ flexShrink: 0 }}
        />
        {(String(role || "").toLowerCase() === "prof" ||
          String(role || "").toLowerCase() === "professor" ||
          String(role || "").toLowerCase() === "university_admin") && (
            <MDBox px={1.5} pt={1} pb={0.25} flexShrink={0}>
              <MDBox mb={1.25}>
                <AppProfileMenu variant="sidenav" collapsed={miniSidenav} />
              </MDBox>
              <MDBox
                sx={{
                  px: 1.25,
                  pt: 1,
                  pb: 1,
                  pb: 0.85,
                  mb: 1.25,
                  borderRadius: 2.5,
                  backgroundColor: textColor === "white" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                  border: textColor === "white" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <MDTypography
                  variant="caption"
                  color={textColor}
                  fontWeight="bold"
                  sx={{ opacity: 0.9, mt: 0.6, display: "block" }}
                >
                  {t("nav.storage")}
                </MDTypography>
                <MDTypography variant="caption" color={textColor} display="block" sx={{ opacity: 0.75, mb: 0.75 }}>
                  {formatBytesToGb(storage.usedBytes)} / {formatBytesToGb(storage.limitBytes)} GB
                </MDTypography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, storage.percent))}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: textColor === "white" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 999,
                      backgroundColor: storage.percent >= 90 ? "#ef5350" : "#29b6f6",
                    },
                  }}
                />
              </MDBox>

              {formatLicenseEnd(storage.licenseEnd) && (
                <MDBox
                  sx={{
                    px: 1.25,
                    py: 0.85,
                    mb: 2,
                    borderRadius: 2.5,
                    backgroundColor: textColor === "white" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                    border: textColor === "white" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon style={{ color: textColor }} sx={{ fontSize: "1.05rem", color: textColor, opacity: 0.9 }}>event</Icon>
                      <MDTypography variant="caption" color={textColor} fontWeight="bold" sx={{ opacity: 0.9 }}>
                        {t("nav.licenseEnds")}
                      </MDTypography>
                    </MDBox>
                    <MDTypography variant="caption" color={textColor} sx={{ opacity: 0.8 }}>
                      {formatLicenseEnd(storage.licenseEnd)}
                    </MDTypography>
                  </MDBox>
                  <LinearProgress
                    variant="determinate"
                    value={licenseProgress.percent}
                    sx={{
                      height: 6,
                      borderRadius: 999,
                      bgcolor: textColor === "white" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        backgroundColor: licenseProgress.color,
                      },
                    }}
                  />
                  <MDTypography variant="caption" color={textColor} sx={{ opacity: 0.75, mt: 0.5, display: "block" }}>
                    {licenseProgress.daysLeft != null
                      ? t("nav.daysLeft", { count: Math.max(0, licenseProgress.daysLeft) })
                      : t("nav.licenseProgress")}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}
        {!(String(role || "").toLowerCase() === "prof" ||
          String(role || "").toLowerCase() === "professor" ||
          String(role || "").toLowerCase() === "university_admin") && (
            <MDBox px={1.25} pt={1.25} pb={0.5} flexShrink={0}>
              <AppProfileMenu variant="sidenav" collapsed={miniSidenav} />
            </MDBox>
          )}
        <MDBox
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(127,127,127,0.45)",
              borderRadius: 999,
            },
          }}
        >
          <List disablePadding>{renderRoutes}</List>
        </MDBox>
        <MDBox
          px={1.25}
          pt={1.5}
          pb={1.5}
          mt="auto"
          flexShrink={0}
          sx={{
            borderTop: textColor === "white" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          {miniSidenav ? (
            <>
              <IconButton
                size="small"
                onClick={(e) => setLangMenuAnchor(e.currentTarget)}
                sx={{
                  color: textColor,
                  "&:hover": { backgroundColor: textColor === "white" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)" },
                }}
                aria-label={t("common.language")}
              >
                <Icon sx={{ fontSize: "1.5rem" }}>language</Icon>
              </IconButton>
              <Menu
                anchorEl={langMenuAnchor}
                open={Boolean(langMenuAnchor)}
                onClose={() => setLangMenuAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                PaperProps={{
                  sx: { mt: 1.5, maxHeight: 480, minHeight: 360, borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.24)", minWidth: 220 },
                }}
              >
                {LANGUAGES.map((code) => (
                  <MenuItem
                    key={code}
                    selected={code === lang}
                    onClick={() => {
                      reduxDispatch(setLang(code));
                      setLangMenuAnchor(null);
                    }}
                  >
                    {t(`languages.${code}`)}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <FormControl fullWidth sx={{ mb: 1.5 }}>
              <Select
                value={lang}
                onChange={(e) => reduxDispatch(setLang(e.target.value))}
                displayEmpty
                size="medium"
                renderValue={(v) => (
                  <MDBox display="flex" alignItems="center" gap={1.25}>
                    <Icon sx={{ fontSize: "1.5rem", opacity: 0.95 }}>language</Icon>
                    <MDTypography variant="button" color={textColor} fontWeight="600" sx={{ textTransform: "none", fontSize: "0.875rem" }}>
                      {t(`languages.${v}`) || v}
                    </MDTypography>
                  </MDBox>
                )}
                sx={{
                  color: textColor,
                  borderRadius: 3,
                  minHeight: 48,
                  backgroundColor: textColor === "white" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: textColor === "white" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: textColor === "white" ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.25)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: textColor === "white" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.4)",
                    borderWidth: "1px",
                  },
                  "& .MuiSvgIcon-root": { color: textColor },
                  fontSize: "0.875rem",
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1.5,
                      maxHeight: 480,
                      minHeight: 360,
                      borderRadius: 2,
                      boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
                    },
                  },
                }}
              >
                {LANGUAGES.map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`languages.${code}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </MDBox>
        <MDBox pt={1} pb={3.5} px={1.25} flexShrink={0}>
          <MDButton
            component="a"
            onClick={performLogout}
            target="_blank"
            rel="noreferrer"
            variant="gradient"
            color={sidenavColor}
            fullWidth
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              py: 1.25,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {t("common.logout")}
          </MDButton>
        </MDBox>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
